import React, { useMemo, useCallback, useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  MenuItem,
  Typography,
  Alert,
  Box,
  Paper,
  Grid,
  Button,
  Tooltip,
  InputAdornment,
  IconButton,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import WarningIcon from '@mui/icons-material/WarningOutlined';
import Inventory2Icon from '@mui/icons-material/Inventory2Outlined';
import SaveIcon from '@mui/icons-material/SaveOutlined';
import { PrintOutlined as PrintIcon } from '@mui/icons-material';
import SummarizeIcon from '@mui/icons-material/Summarize';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleFilledIcon from '@mui/icons-material/CheckCircle';
import CancelFilledIcon from '@mui/icons-material/Cancel';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { normalizeScannedProductCode } from '../../../utils/normalizeScannedProductCode';
import {
  useInventoryTransfer,
  type TransferItem,
  sumTransferItemsCost,
  sumTransferItemsSale,
} from './InventoryTransfer.hooks';
import { InventoryTransfersService } from '../../../api';
import { useTabScopedOpen } from '../../dashboard/TabPanelActiveContext';
import ProductTypeGroup from './ProductTypeGroup';
import InventoryDetailModal from '../InventoryDetailModal';
import type { InventoryItem } from '../../../types/inventory';
import { mapWarehouseInventoryProductApiToInventoryItem } from '../../../utils/inventory/mapWarehouseInventoryProductToInventoryItem';
import { ConfigToggleBox } from '../../sales/SalesStep2/SalesStep2.styles';
import CommonPagination from '../../common/Pagination';
import { BaseTable, TableColumn } from '../../common/Table';
import { StatusChip as StatusChipCommon, ActionButton } from '../../common/Table/Table.styles';
import { TableStatusChip } from '../../common/Table/TableStatusChip';
import EmptyStateMessage from '../../common/EmptyStateMessage';
import { mapTransferStatus } from '../../common/status';
import { StatusSummaryBar, type StatusSummaryMetric } from '../../common/StatusSummaryBar';
import { useInventoryTransferPermissions } from '../../../hooks/useInventoryTransferPermissions';
import { useCanChangeInventoryLocation } from '../../../hooks/useCanChangeInventoryLocation';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../../redux/slices/auth.slice';
import { canAcceptInventoryTransfer } from '../../../utils/inventory/canAcceptInventoryTransfer';
import { ConfirmationModal } from '../../ui/Modal';
import { CloseConfirmationModal } from '../../modals/confirmation';
import { FlagSwitch } from '../../../components/ui';
import {
  ListPageFiltersSection,
  ListPageFilterContainer,
  ListPagePrimaryFilterRow,
  ListPageFilterCollapse,
  ListPageEqualSecondaryFilterRow,
  ListPageFilterTextField,
  ListPageFilterToolbarOutlinedButton,
  ListPageActiveFilters,
  type ListPageActiveFilterChip,
} from '../../common/list-page';
import {
  StyledTextField,
  ModernButton,
  LoadingContainer,
  LoadingSpinner,
  LoadingText,
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  CloseButton,
  DashboardContainer,
  DashboardCard,
  DashboardValue,
  DashboardLabel,
  ProgressBarContainer,
  ProgressBar,
  insetPanelPaperSx,
} from './InventoryTransfer.styles';
import { formatCurrency } from '../../../utils/formatters/money';
import { useNavigationStateOnce } from '../../../hooks/useNavigationStateOnce';
import { HOME_PENDING_STATUS_FILTER } from '../../../constants/homeNavigation.constants';
import { STATUS_COLUMN_SORTABLE } from '../../../constants/listSort.constants';

interface TransferItemsSearchFieldProps {
  value: string;
  onChange: (value: string) => void;
}

const TransferItemsSearchField: React.FC<TransferItemsSearchFieldProps> = ({ value, onChange }) => (
  <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <StyledTextField
      fullWidth
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Buscar por código, nombre o registro..."
      sx={{ flex: 1, minWidth: 0 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ color: 'text.secondary', fontSize: '1.25rem' }} />
          </InputAdornment>
        ),
        endAdornment: value ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={() => onChange('')}
              aria-label="Limpiar búsqueda"
              edge="end"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
    />
  </Box>
);

/** Campo dedicado al lector: código completo + Enter = mismo criterio que selectProductByBarcode. */
interface TransferBarcodeScanFieldProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (trimmedCode: string) => void;
  errorText: string | null;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}

const TransferBarcodeScanField: React.FC<TransferBarcodeScanFieldProps> = ({
  value,
  onChange,
  onSubmit,
  errorText,
  inputRef,
}) => (
  <Box sx={{ mb: 2 }}>
    <StyledTextField
      inputRef={inputRef}
      fullWidth
      size="small"
      label="Escanear código de barras"
      value={value}
      onChange={(e) => onChange(normalizeScannedProductCode(e.target.value))}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
        e.preventDefault();
        const input = e.target as HTMLInputElement;
        const normalized = normalizeScannedProductCode(input.value);
        onChange(normalized);
        onSubmit(normalized);
        // Keep parsed code visible; select-all so the next scan replaces it.
        requestAnimationFrame(() => {
          input.select();
        });
      }}
      placeholder="Escanea la pieza o escribe el código completo y pulsa Enter"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <QrCodeScannerIcon sx={{ color: 'text.secondary', fontSize: '1.25rem' }} />
          </InputAdornment>
        ),
      }}
      InputLabelProps={{ shrink: true }}
      error={Boolean(errorText)}
      helperText={errorText || undefined}
    />
  </Box>
);

// Props interface for the component
export interface InventoryTransferProps {
  onTransferUpdate?: () => void;
}

const InventoryTransfer: React.FC<InventoryTransferProps> = ({ onTransferUpdate }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Transfer permissions - centralized in a single hook
  const transferPerms = useInventoryTransferPermissions();
  const canAccessAllLocations = useCanChangeInventoryLocation();
  const currentUser = useSelector(selectCurrentUser);

  const acceptContext = useMemo(
    () => ({
      canApprove: transferPerms.canApprove,
      canAccessAllLocations,
      userPrimaryLocationId: currentUser?.primaryLocationId,
    }),
    [canAccessAllLocations, currentUser?.primaryLocationId, transferPerms.canApprove],
  );

  const canAcceptTransfer = useCallback(
    (order: { status: string; destinationLocationId: number | null }) =>
      canAcceptInventoryTransfer({
        status: order.status,
        destinationLocationId: order.destinationLocationId,
        ...acceptContext,
      }),
    [acceptContext],
  );

  // Use the custom hook for all state and logic
  const initialHomeStatus = (location.state as { filterTransferStatus?: string } | null)
    ?.filterTransferStatus;
  const {
    // State
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    transferOrders,
    locations,
    filters,
    appliedFilters,
    isLoading,
    isSearching,
    isExporting,
    pagination,
    showMoreFilters,
    showDetailsModal,
    selectedTransferDetail,
    isLoadingDetails,
    showConfirmationDialog,
    notification,
    confirmationType,
    transferIdToConfirm,
    showFinalConfirmModal,
    isConfirming,
    showCloseConfirmation,

    // Conteo y borradores
    selectedProductIds,
    allProductsSelected,
    dashboardMetrics,
    isUpdatingCountingFlag,
    toggleProductSelection,
    selectProductByBarcode,
    saveDraft,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    clearDraft,
    resetCounting,
    updateTransferRequireCounting,
    barcodeInput,
    setBarcodeInput,

    // Computed values
    currentPageItems,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    visiblePages,
    groupedProductsByType,
    productTypes,
    expandedProductTypes,

    // Handlers
    handleFilterChange,
    handleSelectChange,
    handleTransferCodeKeyPress,
    handleResetFilters,
    handleSearch,
    clearAppliedFilterField,
    applyDeepLinkFilter,
    applyStatusFilter,
    applyHomeStatusFilter,
    handleExport,
    handleCloseModal,
    handleViewDetails,
    handlePrintTransfer,
    handleAcceptTransfer,
    handleRejectTransfer,
    handleOpenFinalConfirmation,
    handleConfirmation,
    handleCancelConfirmation,
    handleRequestCloseConfirmation,
    handleConfirmDiscardConfirmation,
    handleCancelDiscardConfirmation,
    handleCancelFinalConfirmation,
    handleCloseNotification,
    toggleMoreFilters,
    handlePageChange,
    handleItemsPerPageChange,
    toggleProductTypeExpansion,
    expandAllProductTypes,
    collapseAllProductTypes,

    // Utilities
    formatDate,
    statusSummary,
    statusSummaryLoading,
    transferSort,
  } = useInventoryTransfer({
    onTransferUpdate,
    showTotalCostAmount: transferPerms.canShowTotalCostAmount,
    showTotalSaleAmount: transferPerms.canShowTotalSaleAmount,
    // Desde Inicio ("Transferencias pendientes"): la primera carga ya sale filtrada.
    initialStatus: initialHomeStatus ?? '',
  });
  const claimHomeNavigation = useNavigationStateOnce(
    initialHomeStatus != null && initialHomeStatus !== '',
  );

  const TRANSFER_SUMMARY_METRICS: StatusSummaryMetric[] = useMemo(
    () => [
      {
        key: 'total',
        label: 'Total transferencias',
        icon: <SummarizeIcon />,
        iconVariant: 'inventory',
        filterValue: '',
      },
      {
        key: 'completed',
        label: 'Completadas',
        icon: <CheckCircleFilledIcon />,
        iconVariant: 'sales',
        filterValue: 'Completado',
      },
      {
        key: 'pending',
        label: 'Pendientes',
        icon: <AccessTimeIcon />,
        iconVariant: 'warning',
        filterValue: HOME_PENDING_STATUS_FILTER.transfers,
      },
      {
        key: 'cancelled',
        label: 'Canceladas',
        icon: <CancelFilledIcon />,
        iconVariant: 'cost',
        filterValue: 'Cancelado',
      },
    ],
    [],
  );

  const scopedDetailsOpen = useTabScopedOpen(showDetailsModal);
  const scopedConfirmationOpen = useTabScopedOpen(showConfirmationDialog);

  // Toggle del flag de conteo (permiso inventory.inventory-transfer.toggle_require_counting)
  const handleToggleCounting = useCallback(
    async (newValue: boolean) => {
      if (!transferIdToConfirm) return;
      try {
        await updateTransferRequireCounting(transferIdToConfirm, newValue);
      } catch (error) {
        console.error('Error al actualizar flag de conteo:', error);
      }
    },
    [transferIdToConfirm, updateTransferRequireCounting],
  );

  const [transferItemsSearch, setTransferItemsSearch] = useState('');
  const [scannerFieldError, setScannerFieldError] = useState<string | null>(null);

  const pieceDetailContextRef = useRef<{ transferId: number; wipId: number } | null>(null);
  const [pieceDetailOpen, setPieceDetailOpen] = useState(false);
  const [pieceDetailItem, setPieceDetailItem] = useState<InventoryItem | null>(null);
  const [pieceDetailFetching, setPieceDetailFetching] = useState(false);
  const [pieceDetailFetchError, setPieceDetailFetchError] = useState<string | null>(null);

  const closePieceDetailModal = useCallback(() => {
    pieceDetailContextRef.current = null;
    setPieceDetailOpen(false);
    setPieceDetailItem(null);
    setPieceDetailFetchError(null);
    setPieceDetailFetching(false);
  }, []);

  const reloadPieceDetailFromTransfer = useCallback(async () => {
    const ctx = pieceDetailContextRef.current;
    if (!ctx) return;
    try {
      const res =
        await InventoryTransfersService.inventoryTransfersControllerFindWarehouseInventoryProductForTransfer(
          ctx.transferId,
          ctx.wipId,
        );
      const data = res?.data ?? res;
      setPieceDetailItem(mapWarehouseInventoryProductApiToInventoryItem(data));
    } catch (e) {
      console.error(e);
    }
  }, []);

  const openPieceDetail = useCallback(
    async (warehouseInventoryProductId: number) => {
      const transferId = selectedTransferDetail?.id ? Number(selectedTransferDetail.id) : null;
      if (!transferId || !warehouseInventoryProductId) return;
      pieceDetailContextRef.current = { transferId, wipId: warehouseInventoryProductId };
      setPieceDetailFetchError(null);
      setPieceDetailItem(null);
      setPieceDetailOpen(false);
      setPieceDetailFetching(true);
      try {
        const res =
          await InventoryTransfersService.inventoryTransfersControllerFindWarehouseInventoryProductForTransfer(
            transferId,
            warehouseInventoryProductId,
          );
        const data = res?.data ?? res;
        setPieceDetailItem(mapWarehouseInventoryProductApiToInventoryItem(data));
        setPieceDetailOpen(true);
      } catch (err: unknown) {
        console.error(err);
        const msg =
          (err as { body?: { message?: string } })?.body?.message ||
          (err as Error)?.message ||
          'No se pudo cargar el detalle de la pieza.';
        setPieceDetailFetchError(msg);
      } finally {
        setPieceDetailFetching(false);
      }
    },
    [selectedTransferDetail?.id],
  );

  useEffect(() => {
    if (!showDetailsModal && !showConfirmationDialog) {
      closePieceDetailModal();
    }
  }, [showDetailsModal, showConfirmationDialog, closePieceDetailModal]);

  useEffect(() => {
    if (!showConfirmationDialog) {
      setScannerFieldError(null);
    }
  }, [showConfirmationDialog]);

  useEffect(() => {
    if (!showDetailsModal && !showConfirmationDialog) {
      setTransferItemsSearch('');
    }
  }, [showDetailsModal, showConfirmationDialog]);

  useEffect(() => {
    if (showConfirmationDialog) {
      setTransferItemsSearch('');
    }
  }, [showConfirmationDialog]);

  useEffect(() => {
    setTransferItemsSearch('');
  }, [selectedTransferDetail?.id]);

  const transferSearchTrim = transferItemsSearch.trim().toLowerCase();

  const { filteredGroupedByType, filteredProductTypes, filteredItemCount } = useMemo(() => {
    if (!selectedTransferDetail?.items?.length) {
      return {
        filteredGroupedByType: {} as Record<string, TransferItem[]>,
        filteredProductTypes: [] as string[],
        filteredItemCount: 0,
      };
    }

    if (!transferSearchTrim) {
      return {
        filteredGroupedByType: groupedProductsByType,
        filteredProductTypes: productTypes,
        filteredItemCount: selectedTransferDetail.items.length,
      };
    }

    const result: Record<string, TransferItem[]> = {};
    let count = 0;
    for (const type of productTypes) {
      const list = groupedProductsByType[type] || [];
      const filtered = list.filter((item) => {
        const code = (item.productCode || '').toLowerCase();
        const name = (item.productName || '').toLowerCase();
        const record = (item.recordCode || '').toLowerCase();
        return (
          code.includes(transferSearchTrim) ||
          name.includes(transferSearchTrim) ||
          record.includes(transferSearchTrim)
        );
      });
      if (filtered.length > 0) {
        result[type] = filtered;
        count += filtered.length;
      }
    }
    return {
      filteredGroupedByType: result,
      filteredProductTypes: Object.keys(result).sort((a, b) => a.localeCompare(b)),
      filteredItemCount: count,
    };
  }, [selectedTransferDetail, groupedProductsByType, productTypes, transferSearchTrim]);

  const transferProductsTitleCount = transferSearchTrim
    ? `${filteredItemCount} de ${selectedTransferDetail?.items.length ?? 0}`
    : (selectedTransferDetail?.items.length ?? 0);

  const transferItemsSearchActive = transferSearchTrim.length > 0;

  const handleBarcodeScanSubmit = useCallback(
    (code: string) => {
      if (!code) return;
      const found = selectProductByBarcode(code);
      setScannerFieldError(found ? null : 'Código no encontrado en esta transferencia');
    },
    [selectProductByBarcode],
  );

  const handleBarcodeInputChange = useCallback(
    (value: string) => {
      setBarcodeInput(normalizeScannedProductCode(value));
      setScannerFieldError(null);
    },
    [setBarcodeInput],
  );

  // Deep-link: filtrar por código TRL / estado desde Inicio; abrir detalle
  useEffect(() => {
    const state = location.state as {
      openTransferDetails?: number;
      filterTransferCode?: string;
      filterTransferStatus?: string;
    } | null;
    if (
      state?.openTransferDetails == null &&
      !state?.filterTransferCode &&
      !state?.filterTransferStatus
    ) {
      return;
    }

    // Clic en la tarjeta de Inicio con la pestaña ya abierta: vista limpia con solo ese estado.
    // En pestaña nueva ya vino como estado inicial del hook.
    if (state.filterTransferStatus && claimHomeNavigation(location.key)) {
      applyHomeStatusFilter(state.filterTransferStatus);
    }
    if (state.filterTransferCode) {
      applyDeepLinkFilter(state.filterTransferCode);
    }
    if (state.openTransferDetails != null && handleViewDetails) {
      handleViewDetails(state.openTransferDetails.toString());
    }

    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: {} },
    );
  }, [
    location.state,
    claimHomeNavigation,
    location.key,
    location.pathname,
    location.search,
    handleViewDetails,
    applyDeepLinkFilter,
    applyHomeStatusFilter,
    navigate,
  ]);

  const columns: TableColumn<(typeof currentPageItems)[number]>[] = useMemo(
    () => [
      {
        id: 'transferCode',
        label: 'Código de Transferencia',
        sortField: 'transferCode',
        render: (item) => (
          <Typography color="text.secondary" fontWeight={600} fontSize="0.95rem">
            {item.transferCode}
          </Typography>
        ),
      },
      {
        id: 'source',
        label: 'Ubicación Origen',
        sortField: 'sourceLocationName',
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {item.source}
          </Typography>
        ),
      },
      {
        id: 'destination',
        label: 'Ubicación Destino',
        sortField: 'destinationLocationName',
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {item.destination}
          </Typography>
        ),
      },
      {
        id: 'createdBy',
        label: 'Creado Por',
        sortField: 'createdByName',
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {item.createdBy || '—'}
          </Typography>
        ),
      },
      {
        id: 'date',
        label: 'Fecha Transferencia',
        sortField: 'date',
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {formatDate(item.date)}
          </Typography>
        ),
      },
      {
        id: 'productCount',
        label: 'Cantidad de piezas',
        sortField: 'itemCount',
        align: 'center',
        render: (item) => (
          <Typography color="text.primary" fontWeight={600} fontSize="0.95rem">
            {item.productCount ?? 0}
          </Typography>
        ),
      },
      {
        id: 'status',
        label: 'Estado',
        sortField: 'status',
        sortable: STATUS_COLUMN_SORTABLE,
        render: (item) =>
          (() => {
            const chip = mapTransferStatus(item.status);
            return <StatusChipCommon label={chip.label} size="small" color={chip.color} />;
          })(),
      },
    ],
    [formatDate],
  );

  const activeFilterChips: ListPageActiveFilterChip[] = useMemo(() => {
    const chips: ListPageActiveFilterChip[] = [];
    const locName = (id: string) => locations.find((l) => l.id.toString() === id)?.name || id;

    if (appliedFilters.transferCode) {
      chips.push({
        key: 'transferCode',
        label: `Código: ${appliedFilters.transferCode}`,
        onRemove: () => clearAppliedFilterField('transferCode'),
      });
    }
    if (appliedFilters.sourceLocation) {
      chips.push({
        key: 'sourceLocation',
        label: `Origen: ${locName(appliedFilters.sourceLocation)}`,
        onRemove: () => clearAppliedFilterField('sourceLocation'),
      });
    }
    if (appliedFilters.location) {
      chips.push({
        key: 'location',
        label: `Destino: ${locName(appliedFilters.location)}`,
        onRemove: () => clearAppliedFilterField('location'),
      });
    }
    if (appliedFilters.status) {
      chips.push({
        key: 'status',
        label: `Estado: ${mapTransferStatus(appliedFilters.status).label}`,
        onRemove: () => clearAppliedFilterField('status'),
      });
    }
    if (appliedFilters.dateFrom) {
      chips.push({
        key: 'dateFrom',
        label: `Desde: ${appliedFilters.dateFrom}`,
        onRemove: () => clearAppliedFilterField('dateFrom'),
      });
    }
    if (appliedFilters.dateTo) {
      chips.push({
        key: 'dateTo',
        label: `Hasta: ${appliedFilters.dateTo}`,
        onRemove: () => clearAppliedFilterField('dateTo'),
      });
    }
    return chips;
  }, [appliedFilters, locations, clearAppliedFilterField]);

  if (!transferPerms.canView) {
    return null;
  }

  return (
    <>
      <StatusSummaryBar
        metrics={TRANSFER_SUMMARY_METRICS}
        counts={{
          total: statusSummary.total,
          completed: statusSummary.completed,
          pending: statusSummary.pending,
          cancelled: statusSummary.cancelled,
        }}
        loading={statusSummaryLoading}
        activeFilterValue={appliedFilters.status}
        onMetricClick={applyStatusFilter}
      />

      <ListPageFiltersSection>
        <ListPageFilterContainer>
          <ListPagePrimaryFilterRow
            showMoreFilters={showMoreFilters}
            onToggleMoreFilters={toggleMoreFilters}
            onReset={handleResetFilters}
            onSearch={handleSearch}
            searchFieldProps={{
              label: 'Código de Transferencia',
              name: 'transferCode',
              value: filters.transferCode,
              onChange: handleFilterChange,
              onKeyDown: handleTransferCodeKeyPress,
            }}
            trailingActions={
              transferPerms.canExport && (
                <ListPageFilterToolbarOutlinedButton
                  variant="outlined"
                  size="medium"
                  onClick={handleExport}
                  disabled={isExporting}
                  startIcon={<DownloadIcon />}
                >
                  {isExporting ? 'Exportando...' : 'Exportar'}
                </ListPageFilterToolbarOutlinedButton>
              )
            }
          />

          <ListPageFilterCollapse in={showMoreFilters} timeout={350} unmountOnExit>
            <ListPageEqualSecondaryFilterRow>
              <ListPageFilterTextField
                fullWidth
                select
                variant="outlined"
                size="medium"
                label="Ubicación Origen"
                name="sourceLocation"
                value={filters.sourceLocation}
                onChange={handleSelectChange}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">
                  <em>Todas</em>
                </MenuItem>
                {locations.map((location) => (
                  <MenuItem key={location.id} value={location.id.toString()}>
                    {location.name}
                  </MenuItem>
                ))}
              </ListPageFilterTextField>

              <ListPageFilterTextField
                fullWidth
                select
                variant="outlined"
                size="medium"
                label="Ubicación Destino"
                name="location"
                value={filters.location}
                onChange={handleSelectChange}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">
                  <em>Todas</em>
                </MenuItem>
                {locations.map((location) => (
                  <MenuItem key={location.id} value={location.id.toString()}>
                    {location.name}
                  </MenuItem>
                ))}
              </ListPageFilterTextField>

              <ListPageFilterTextField
                fullWidth
                select
                variant="outlined"
                size="medium"
                label="Estado"
                name="status"
                value={filters.status}
                onChange={handleSelectChange}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>
                <MenuItem value="Pendiente">Pendiente</MenuItem>
                <MenuItem value="Completado">Completado</MenuItem>
                <MenuItem value="Cancelado">Cancelado</MenuItem>
              </ListPageFilterTextField>

              <ListPageFilterTextField
                fullWidth
                variant="outlined"
                size="medium"
                label="Fecha de Creación Desde"
                type="date"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />

              <ListPageFilterTextField
                fullWidth
                variant="outlined"
                size="medium"
                label="Fecha de Creación Hasta"
                type="date"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            </ListPageEqualSecondaryFilterRow>
          </ListPageFilterCollapse>

          <ListPageActiveFilters chips={activeFilterChips} />
        </ListPageFilterContainer>
      </ListPageFiltersSection>

      {/* Transfer Orders Table - Opción 4: Sin Paper, borde y fondo sutil */}
      {!isLoading && currentPageItems.length > 0 && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
            border: 'none',
            bgcolor: 'transparent',
            overflow: 'visible',
            minHeight: 0,
            px: 0,
          }}
        >
          <BaseTable
            items={currentPageItems}
            columns={columns}
            selectedItems={[]}
            onSelectItems={() => {}}
            showCheckbox={false}
            showActions
            getItemId={(item) => item.id}
            emptyStateTitle="No se encontraron transferencias"
            emptyStateSubtitle="Ajuste los filtros o intente de nuevo."
            sortBy={transferSort.sortBy}
            sortOrder={transferSort.sortOrder}
            onRequestSort={transferSort.requestSort}
            renderActions={(order) => (
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                {transferPerms.canViewRow && (
                  <Tooltip title="Ver detalles">
                    <ActionButton size="small" onClick={() => handleViewDetails(order.id)}>
                      <VisibilityIcon fontSize="small" />
                    </ActionButton>
                  </Tooltip>
                )}
                <Tooltip title="Imprimir">
                  <ActionButton size="small" onClick={() => handlePrintTransfer(order.id)}>
                    <PrintIcon fontSize="small" />
                  </ActionButton>
                </Tooltip>
                {order.status === 'Pendiente' && (
                  <>
                    {transferPerms.canViewRow && canAcceptTransfer(order) && (
                      <Tooltip title="Aceptar">
                        <ActionButton size="small" onClick={() => handleAcceptTransfer(order.id)}>
                          <CheckCircleIcon fontSize="small" />
                        </ActionButton>
                      </Tooltip>
                    )}
                    {transferPerms.canViewRow && transferPerms.canReject && (
                      <Tooltip title="Rechazar">
                        <ActionButton
                          colorVariant="error"
                          size="small"
                          onClick={() => handleRejectTransfer(order.id)}
                        >
                          <CancelIcon fontSize="small" />
                        </ActionButton>
                      </Tooltip>
                    )}
                  </>
                )}
              </Box>
            )}
          />
        </Box>
      )}

      {/* Loading state */}
      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 4,
            minHeight: '200px',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <LoadingSpinner />
          <LoadingText>Cargando transferencias...</LoadingText>
        </Box>
      )}

      {/* Searching state */}
      {isSearching && !isLoading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 4,
            minHeight: '200px',
          }}
        >
          <LoadingSpinner />
        </Box>
      )}

      {/* Empty state */}
      {!isLoading && !isSearching && currentPageItems.length === 0 && (
        <EmptyStateMessage message="No se encontraron transferencias" minHeight={200} />
      )}

      {/* Paginación uniforme - Opción 4: Sin Paper, sin borde superior */}
      {!isLoading && !isSearching && pagination.total > 0 && (
        <Box>
          <CommonPagination
            currentPage={pagination.currentPage}
            itemsPerPage={pagination.itemsPerPage}
            totalItems={pagination.total}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </Box>
      )}

      {/* Modal de Detalles */}
      {/* Modal de Detalles */}
      <StyledDialog
        open={scopedDetailsOpen}
        onClose={handleCloseModal}
        aria-labelledby="details-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <StyledDialogTitle id="details-dialog-title">
          <Typography variant="h6" component="span">
            Detalles de Transferencia
          </Typography>
          <CloseButton onClick={handleCloseModal}>
            <CloseIcon />
          </CloseButton>
        </StyledDialogTitle>

        <StyledDialogContent>
          {isLoadingDetails ? (
            <LoadingContainer>
              <LoadingSpinner />
              <LoadingText>Cargando detalles de la transferencia...</LoadingText>
            </LoadingContainer>
          ) : selectedTransferDetail ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Información de la transferencia */}
              <Paper sx={(theme) => insetPanelPaperSx(theme)}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
                  Detalles de la Transferencia
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                    >
                      Código de Transferencia
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {selectedTransferDetail.transferCode}
                    </Typography>
                  </Grid>
                  {selectedTransferDetail.inventoryRequestCode &&
                    selectedTransferDetail.inventoryRequestId && (
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            fontWeight: 500,
                            color: 'text.secondary',
                            mb: 0.5,
                          }}
                        >
                          Solicitud origen
                        </Typography>
                        <Button
                          variant="text"
                          size="small"
                          sx={{ p: 0, minWidth: 0, fontWeight: 600, textTransform: 'none' }}
                          onClick={() => {
                            handleCloseModal();
                            navigate('/dashboard/inventory/requests', {
                              state: {
                                openRequestDetails: selectedTransferDetail.inventoryRequestId,
                                filterRequestCode: selectedTransferDetail.inventoryRequestCode,
                              },
                            });
                          }}
                        >
                          {selectedTransferDetail.inventoryRequestCode}
                        </Button>
                      </Grid>
                    )}
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                    >
                      Estado Actual
                    </Typography>
                    {(() => {
                      const chip = mapTransferStatus(selectedTransferDetail.status);
                      return <TableStatusChip label={chip.label} color={chip.color} size="small" />;
                    })()}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                    >
                      Ubicación Origen
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {selectedTransferDetail.source}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                    >
                      Ubicación Destino
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {selectedTransferDetail.destination}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                    >
                      Fecha de Transferencia
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {selectedTransferDetail.date}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                    >
                      Creado Por
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {selectedTransferDetail.createdBy}
                    </Typography>
                  </Grid>
                  {selectedTransferDetail.completedBy && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                      >
                        Aceptado por
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.primary' }}>
                        {selectedTransferDetail.completedBy}
                      </Typography>
                    </Grid>
                  )}
                  {selectedTransferDetail.cancelledBy && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                      >
                        Cancelado por
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.primary' }}>
                        {selectedTransferDetail.cancelledBy}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                    >
                      Cantidad de piezas
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {selectedTransferDetail.items.length}
                    </Typography>
                  </Grid>
                  {transferPerms.canShowTotalCostAmount && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                      >
                        Monto Total de Costo
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1.1rem' }}
                      >
                        {formatCurrency(sumTransferItemsCost(selectedTransferDetail.items))}
                      </Typography>
                    </Grid>
                  )}
                  {transferPerms.canShowTotalSaleAmount && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                      >
                        Monto Total de Venta
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: 'success.main', fontSize: '1.1rem' }}
                      >
                        {formatCurrency(sumTransferItemsSale(selectedTransferDetail.items))}
                      </Typography>
                    </Grid>
                  )}
                  {selectedTransferDetail.notes && (
                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                      >
                        Notas / Observaciones
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}
                      >
                        {selectedTransferDetail.notes}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Lista de productos agrupados por tipo */}
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Piezas en transferencia ({transferProductsTitleCount})
                  </Typography>
                  {productTypes.length > 1 && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        onClick={expandAllProductTypes}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                        }}
                      >
                        Expandir todos
                      </Button>
                      <Button
                        size="small"
                        onClick={collapseAllProductTypes}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                        }}
                      >
                        Colapsar todos
                      </Button>
                    </Box>
                  )}
                </Box>
                {selectedTransferDetail.items.length > 0 && (
                  <TransferItemsSearchField
                    value={transferItemsSearch}
                    onChange={setTransferItemsSearch}
                  />
                )}
                {selectedTransferDetail.items.length > 0 ? (
                  <Box>
                    {transferItemsSearchActive && filteredProductTypes.length === 0 ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 2, textAlign: 'center' }}
                      >
                        No hay piezas que coincidan con la búsqueda.
                      </Typography>
                    ) : (
                      filteredProductTypes.map((type) => {
                        const products = filteredGroupedByType[type] || [];
                        const isExpanded =
                          transferItemsSearchActive || expandedProductTypes.has(type);
                        return (
                          <ProductTypeGroup
                            key={type}
                            productType={type}
                            products={products}
                            isExpanded={isExpanded}
                            onToggle={() => toggleProductTypeExpansion(type)}
                            confirmationType="view"
                            onViewPieceDetail={openPieceDetail}
                          />
                        );
                      })
                    )}
                  </Box>
                ) : (
                  <Paper
                    sx={(theme) => ({
                      textAlign: 'center',
                      py: 4,
                      ...insetPanelPaperSx(theme),
                    })}
                  >
                    <WarningIcon
                      sx={{
                        fontSize: '3rem',
                        color: 'text.disabled',
                        mb: 1,
                        mx: 'auto',
                        display: 'block',
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                    >
                      No se encontraron piezas en esta transferencia
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      La transferencia puede estar vacía o los datos no se cargaron bien.
                    </Typography>
                  </Paper>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <Typography>No se pudieron cargar los detalles de la transferencia</Typography>
            </Box>
          )}
        </StyledDialogContent>

        <StyledDialogActions>
          {/* Botón Imprimir eliminado - Ya existe acción de fila para imprimir en la tabla */}
          <ModernButton onClick={handleCloseModal} variant="tonal">
            Cerrar
          </ModernButton>
        </StyledDialogActions>
      </StyledDialog>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 2 }}
        open={pieceDetailFetching}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Dialog
        open={Boolean(pieceDetailFetchError)}
        onClose={() => setPieceDetailFetchError(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Alert severity="error">{pieceDetailFetchError}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPieceDetailFetchError(null)} color="inherit">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <InventoryDetailModal
        open={pieceDetailOpen && Boolean(pieceDetailItem)}
        onClose={closePieceDetailModal}
        item={pieceDetailItem}
        onEditItem={() => {}}
        onSave={() => {
          void reloadPieceDetailFromTransfer();
          onTransferUpdate?.();
        }}
        onItemUpdate={(updated) => setPieceDetailItem(updated)}
      />

      {/* Modal de Confirmación */}
      <StyledDialog
        open={scopedConfirmationOpen}
        onClose={handleRequestCloseConfirmation}
        aria-labelledby="confirmation-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <StyledDialogTitle id="confirmation-dialog-title">
          <Typography variant="h6" component="span">
            {confirmationType === 'accept' ? 'Confirmar Aceptación' : 'Confirmar Rechazo'}
          </Typography>
          <CloseButton onClick={handleRequestCloseConfirmation}>
            <CloseIcon />
          </CloseButton>
        </StyledDialogTitle>

        <StyledDialogContent>
          {isLoadingDetails ? (
            <LoadingContainer>
              <LoadingSpinner />
              <LoadingText>Cargando detalles de la transferencia...</LoadingText>
            </LoadingContainer>
          ) : selectedTransferDetail ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {/* Toggle de configuración de conteo (permiso de flujo) */}
              {confirmationType === 'accept' &&
                transferPerms.canToggleRequireCounting &&
                selectedTransferDetail && (
                  <ConfigToggleBox isActive={selectedTransferDetail.requireCounting ?? true}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Inventory2Icon
                          color={
                            (selectedTransferDetail.requireCounting ?? true) ? 'success' : 'action'
                          }
                          sx={{ fontSize: 16 }}
                        />
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          color={
                            (selectedTransferDetail.requireCounting ?? true)
                              ? 'success.main'
                              : 'text.secondary'
                          }
                          sx={{ fontSize: '0.75rem' }}
                        >
                          Requiere Conteo Manual
                        </Typography>
                      </Box>
                      <FlagSwitch
                        checked={selectedTransferDetail.requireCounting ?? true}
                        onChange={(e, checked) => handleToggleCounting(checked)}
                        disabled={isUpdatingCountingFlag}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}
                    >
                      {(selectedTransferDetail.requireCounting ?? true)
                        ? 'El conteo manual de piezas es requerido antes de aceptar esta transferencia'
                        : 'El conteo manual está desactivado. Puedes aceptar esta transferencia directamente'}
                    </Typography>
                  </ConfigToggleBox>
                )}

              {/* Información de la transferencia */}
              <Paper sx={(theme) => insetPanelPaperSx(theme)}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
                  Detalles de la Transferencia
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                    >
                      Código de Transferencia
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {selectedTransferDetail.transferCode}
                    </Typography>
                  </Grid>
                  {selectedTransferDetail.inventoryRequestCode &&
                    selectedTransferDetail.inventoryRequestId && (
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            fontWeight: 500,
                            color: 'text.secondary',
                            mb: 0.5,
                          }}
                        >
                          Solicitud origen
                        </Typography>
                        <Button
                          variant="text"
                          size="small"
                          sx={{ p: 0, minWidth: 0, fontWeight: 600, textTransform: 'none' }}
                          onClick={() => {
                            handleCloseModal();
                            navigate('/dashboard/inventory/requests', {
                              state: {
                                openRequestDetails: selectedTransferDetail.inventoryRequestId,
                                filterRequestCode: selectedTransferDetail.inventoryRequestCode,
                              },
                            });
                          }}
                        >
                          {selectedTransferDetail.inventoryRequestCode}
                        </Button>
                      </Grid>
                    )}
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                    >
                      Estado Actual
                    </Typography>
                    {(() => {
                      const chip = mapTransferStatus(selectedTransferDetail.status);
                      return <TableStatusChip label={chip.label} color={chip.color} size="small" />;
                    })()}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                    >
                      Ubicación Origen
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {selectedTransferDetail.source}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                    >
                      Ubicación Destino
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {selectedTransferDetail.destination}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                    >
                      Fecha de Transferencia
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {selectedTransferDetail.date}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                    >
                      Creado Por
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {selectedTransferDetail.createdBy}
                    </Typography>
                  </Grid>
                  {selectedTransferDetail.completedBy && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                      >
                        Aceptado por
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.primary' }}>
                        {selectedTransferDetail.completedBy}
                      </Typography>
                    </Grid>
                  )}
                  {selectedTransferDetail.cancelledBy && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                      >
                        Cancelado por
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.primary' }}>
                        {selectedTransferDetail.cancelledBy}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                    >
                      Cantidad de piezas
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {selectedTransferDetail.items.length}
                    </Typography>
                  </Grid>
                  {transferPerms.canShowTotalCostAmount && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                      >
                        Monto Total de Costo
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1.1rem' }}
                      >
                        {formatCurrency(sumTransferItemsCost(selectedTransferDetail.items))}
                      </Typography>
                    </Grid>
                  )}
                  {transferPerms.canShowTotalSaleAmount && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                      >
                        Monto Total de Venta
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: 'success.main', fontSize: '1.1rem' }}
                      >
                        {formatCurrency(sumTransferItemsSale(selectedTransferDetail.items))}
                      </Typography>
                    </Grid>
                  )}
                  {selectedTransferDetail.notes && (
                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                      >
                        Notas / Observaciones
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}
                      >
                        {selectedTransferDetail.notes}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Lista de productos agrupados por tipo */}
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Piezas en transferencia ({transferProductsTitleCount})
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {productTypes.length > 1 && (
                      <>
                        <Button
                          size="small"
                          onClick={expandAllProductTypes}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                          }}
                        >
                          Expandir todos
                        </Button>
                        <Button
                          size="small"
                          onClick={collapseAllProductTypes}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                          }}
                        >
                          Colapsar todos
                        </Button>
                      </>
                    )}
                    {confirmationType === 'accept' &&
                      selectedProductIds.size > 0 &&
                      transferIdToConfirm && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            resetCounting(transferIdToConfirm);
                          }}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            borderColor: 'divider',
                            color: 'text.secondary',
                            '&:hover': {
                              borderColor: 'text.secondary',
                            },
                          }}
                        >
                          Restablecer
                        </Button>
                      )}
                  </Box>
                </Box>
                {confirmationType === 'accept' &&
                  (selectedTransferDetail?.requireCounting ?? true) &&
                  selectedTransferDetail.items.length > 0 && (
                    <TransferBarcodeScanField
                      value={barcodeInput}
                      onChange={handleBarcodeInputChange}
                      onSubmit={handleBarcodeScanSubmit}
                      errorText={scannerFieldError}
                    />
                  )}

                {/* Dashboard de Conteo - debajo del escaneo para ver progreso al aceptar */}
                {confirmationType === 'accept' &&
                  (selectedTransferDetail?.requireCounting ?? true) &&
                  selectedTransferDetail && (
                    <Box sx={{ position: 'relative', mb: 2 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}
                      >
                        Dashboard de Conteo
                      </Typography>

                      <DashboardContainer
                        sx={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, padding: 1.5 }}
                      >
                        <DashboardCard>
                          <DashboardValue sx={{ color: 'primary.main', fontSize: '1.5rem' }}>
                            {dashboardMetrics.total}
                          </DashboardValue>
                          <DashboardLabel sx={{ fontSize: '0.75rem' }}>Total piezas</DashboardLabel>
                        </DashboardCard>

                        <DashboardCard>
                          <DashboardValue sx={{ color: 'success.main', fontSize: '1.5rem' }}>
                            {dashboardMetrics.selected}
                          </DashboardValue>
                          <DashboardLabel sx={{ fontSize: '0.75rem' }}>Verificados</DashboardLabel>
                        </DashboardCard>

                        <DashboardCard>
                          <DashboardValue sx={{ color: 'warning.main', fontSize: '1.5rem' }}>
                            {dashboardMetrics.pending}
                          </DashboardValue>
                          <DashboardLabel sx={{ fontSize: '0.75rem' }}>Pendientes</DashboardLabel>
                        </DashboardCard>

                        <DashboardCard>
                          <DashboardValue sx={{ color: 'info.main', fontSize: '1.5rem' }}>
                            {dashboardMetrics.percentage}%
                          </DashboardValue>
                          <DashboardLabel sx={{ fontSize: '0.75rem' }}>Progreso</DashboardLabel>
                        </DashboardCard>
                      </DashboardContainer>

                      <Box sx={{ mt: 2 }}>
                        <ProgressBarContainer>
                          <ProgressBar percentage={dashboardMetrics.percentage} />
                        </ProgressBarContainer>
                      </Box>
                    </Box>
                  )}

                {selectedTransferDetail.items.length > 0 ? (
                  <Box>
                    {transferItemsSearchActive && filteredProductTypes.length === 0 ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 2, textAlign: 'center' }}
                      >
                        No hay piezas que coincidan con la búsqueda.
                      </Typography>
                    ) : (
                      filteredProductTypes.map((type) => {
                        const products = filteredGroupedByType[type] || [];
                        const isExpanded =
                          transferItemsSearchActive || expandedProductTypes.has(type);
                        const showCheckboxes =
                          confirmationType === 'accept' &&
                          (selectedTransferDetail?.requireCounting ?? true);
                        return (
                          <ProductTypeGroup
                            key={type}
                            productType={type}
                            products={products}
                            isExpanded={isExpanded}
                            onToggle={() => toggleProductTypeExpansion(type)}
                            showCheckboxes={showCheckboxes}
                            selectedProductIds={selectedProductIds}
                            onProductToggle={toggleProductSelection}
                            confirmationType={confirmationType}
                            onViewPieceDetail={openPieceDetail}
                          />
                        );
                      })
                    )}
                  </Box>
                ) : (
                  <Paper
                    sx={(theme) => ({
                      textAlign: 'center',
                      py: 4,
                      ...insetPanelPaperSx(theme),
                    })}
                  >
                    <WarningIcon
                      sx={{
                        fontSize: '3rem',
                        color: 'text.disabled',
                        mb: 1,
                        mx: 'auto',
                        display: 'block',
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}
                    >
                      No se encontraron piezas en esta transferencia
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      La transferencia puede estar vacía o los datos no se cargaron bien.
                    </Typography>
                  </Paper>
                )}
              </Box>

              {/* Mensaje adicional para rechazo */}
              {confirmationType === 'reject' && (
                <Alert
                  severity="warning"
                  icon={<WarningIcon />}
                  sx={(theme) => ({
                    bgcolor: alpha(
                      theme.palette.warning.main,
                      theme.palette.mode === 'dark' ? 0.15 : 0.08,
                    ),
                    border: '1px solid',
                    borderColor: alpha(theme.palette.warning.main, 0.35),
                    borderRadius: 1,
                    '& .MuiAlert-icon': {
                      color: theme.palette.warning.main,
                    },
                  })}
                >
                  <Typography
                    variant="subtitle2"
                    sx={(theme) => ({
                      fontWeight: 500,
                      color:
                        theme.palette.mode === 'dark'
                          ? theme.palette.warning.light
                          : theme.palette.warning.dark,
                      mb: 1,
                    })}
                  >
                    Consideraciones importantes
                  </Typography>
                  <Box
                    component="ul"
                    sx={(theme) => ({
                      pl: 3,
                      m: 0,
                      color:
                        theme.palette.mode === 'dark'
                          ? theme.palette.warning.light
                          : theme.palette.warning.dark,
                    })}
                  >
                    <li>La transferencia será marcada como cancelada</li>
                    <li>Las piezas volverán a estar disponibles en la sucursal de origen</li>
                    <li>Se notificará al usuario que creó la transferencia</li>
                  </Box>
                </Alert>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <Typography>No se pudieron cargar los detalles de la transferencia</Typography>
            </Box>
          )}
        </StyledDialogContent>

        <StyledDialogActions>
          <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
            {/* Botón Guardar Borrador - Solo para aceptación con conteo requerido */}
            {confirmationType === 'accept' &&
              (selectedTransferDetail?.requireCounting ?? true) &&
              transferIdToConfirm &&
              selectedTransferDetail && (
                <ModernButton
                  onClick={() => {
                    saveDraft(transferIdToConfirm, selectedTransferDetail.transferCode);
                    handleCancelConfirmation();
                  }}
                  variant="outlined"
                  sx={{
                    borderColor: 'divider',
                    color: 'text.secondary',
                    '&:hover': {
                      borderColor: 'text.secondary',
                    },
                  }}
                  startIcon={<SaveIcon />}
                >
                  Guardar Borrador
                </ModernButton>
              )}

            <ModernButton variant="tonal" onClick={handleRequestCloseConfirmation}>
              Cancelar
            </ModernButton>

            <ModernButton
              onClick={handleOpenFinalConfirmation}
              variant="contained"
              disabled={
                confirmationType === 'accept' &&
                (selectedTransferDetail?.requireCounting ?? true) &&
                !allProductsSelected
              }
              sx={(theme) => {
                const canSubmit =
                  confirmationType !== 'accept' ||
                  allProductsSelected ||
                  !(selectedTransferDetail?.requireCounting ?? true);
                return {
                  background: canSubmit
                    ? 'linear-gradient(to right, #06b6d4, #10b981)'
                    : theme.palette.action.disabledBackground,
                  color: theme.palette.common.white,
                  marginLeft: 'auto',
                  '&:hover': {
                    background: canSubmit
                      ? 'linear-gradient(to right, #0891b2, #059669)'
                      : theme.palette.action.disabledBackground,
                  },
                  '&:disabled': {
                    background: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                    cursor: 'not-allowed',
                  },
                };
              }}
            >
              {confirmationType === 'accept' ? 'Aceptar Transferencia' : 'Rechazar Transferencia'}
            </ModernButton>
          </Box>
        </StyledDialogActions>
      </StyledDialog>

      <CloseConfirmationModal
        open={showCloseConfirmation}
        onClose={handleCancelDiscardConfirmation}
        onConfirm={handleConfirmDiscardConfirmation}
      />

      <ConfirmationModal
        open={showFinalConfirmModal}
        onClose={handleCancelFinalConfirmation}
        onConfirm={handleConfirmation}
        title={confirmationType === 'accept' ? '¿Confirmar transferencia?' : '¿Confirmar rechazo?'}
        message={
          confirmationType === 'accept'
            ? '¿Desea aceptar esta transferencia?'
            : '¿Desea rechazar esta transferencia?'
        }
        confirmText={confirmationType === 'accept' ? 'Confirmar' : 'Rechazar'}
        cancelText="Cancelar"
        icon={confirmationType === 'accept' ? <CheckCircleIcon /> : <CancelIcon />}
        variant="default"
        loading={isConfirming}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default InventoryTransfer;
